import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tag, Plus, Trash2, Percent, DollarSign } from 'lucide-react';
import { usePricingRules, useCreatePricingRule, useDeletePricingRule, Product, PricingRule } from '@/hooks/useProducts';
import { Skeleton } from '@/components/ui/skeleton';

interface PricingRulesDialogProps {
  product: Product;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ruleTypes = [
  { value: 'discount', label: 'Discount' },
  { value: 'markup', label: 'Markup' },
  { value: 'volume', label: 'Volume Discount' },
  { value: 'bundle', label: 'Bundle Price' },
];

export function PricingRulesDialog({ product, open, onOpenChange }: PricingRulesDialogProps) {
  const { data: rules, isLoading } = usePricingRules(product.id);
  const createRule = useCreatePricingRule();
  const deleteRule = useDeletePricingRule();

  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [ruleType, setRuleType] = useState('discount');
  const [value, setValue] = useState('');
  const [valueType, setValueType] = useState('percentage');
  const [minQuantity, setMinQuantity] = useState('');
  const [maxQuantity, setMaxQuantity] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [priority, setPriority] = useState('0');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const condition: Record<string, number> = {};
    if (minQuantity) condition.min_quantity = parseInt(minQuantity);
    if (maxQuantity) condition.max_quantity = parseInt(maxQuantity);

    await createRule.mutateAsync({
      product_id: product.id,
      name,
      rule_type: ruleType,
      value: parseFloat(value) || 0,
      value_type: valueType,
      condition,
      priority: parseInt(priority) || 0,
      is_active: isActive,
      valid_from: null,
      valid_until: null,
      metadata: {},
    });

    resetForm();
  };

  const resetForm = () => {
    setShowForm(false);
    setName('');
    setRuleType('discount');
    setValue('');
    setValueType('percentage');
    setMinQuantity('');
    setMaxQuantity('');
    setIsActive(true);
    setPriority('0');
  };

  const handleDelete = async (rule: PricingRule) => {
    await deleteRule.mutateAsync(rule.id);
  };

  const formatRuleValue = (rule: PricingRule) => {
    if (rule.value_type === 'percentage') {
      return `${rule.value}%`;
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: product.currency,
    }).format(rule.value);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tag className="h-5 w-5 text-primary" />
            Pricing Rules for {product.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Existing Rules */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Active Rules</Label>
              {!showForm && (
                <Button size="sm" variant="outline" onClick={() => setShowForm(true)}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Rule
                </Button>
              )}
            </div>

            {isLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            ) : rules?.length === 0 ? (
              <div className="text-center py-6 text-muted-foreground border border-dashed rounded-lg">
                No pricing rules configured
              </div>
            ) : (
              <div className="space-y-2">
                {rules?.map((rule) => {
                  const condition = rule.condition as { min_quantity?: number; max_quantity?: number };
                  return (
                    <div
                      key={rule.id}
                      className="flex items-center justify-between p-3 rounded-lg border bg-card"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${rule.rule_type === 'discount' ? 'bg-green-500/10 text-green-500' : 'bg-amber-500/10 text-amber-500'}`}>
                          {rule.value_type === 'percentage' ? (
                            <Percent className="h-4 w-4" />
                          ) : (
                            <DollarSign className="h-4 w-4" />
                          )}
                        </div>
                        <div>
                          <div className="font-medium">{rule.name}</div>
                          <div className="text-sm text-muted-foreground">
                            {rule.rule_type === 'discount' ? '-' : '+'}{formatRuleValue(rule)}
                            {condition.min_quantity && ` • Min qty: ${condition.min_quantity}`}
                            {condition.max_quantity && ` • Max qty: ${condition.max_quantity}`}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={rule.is_active ? 'default' : 'secondary'}>
                          {rule.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleDelete(rule)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Add Rule Form */}
          {showForm && (
            <form onSubmit={handleSubmit} className="space-y-4 border-t pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="ruleName">Rule Name *</Label>
                  <Input
                    id="ruleName"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g., Early Bird Discount"
                    required
                  />
                </div>

                <div>
                  <Label>Rule Type</Label>
                  <Select value={ruleType} onValueChange={setRuleType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ruleTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Value Type</Label>
                  <Select value={valueType} onValueChange={setValueType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">Percentage</SelectItem>
                      <SelectItem value="fixed">Fixed Amount</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="value">Value *</Label>
                  <Input
                    id="value"
                    type="number"
                    step="0.01"
                    min="0"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    placeholder={valueType === 'percentage' ? '10' : '100'}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="priority">Priority</Label>
                  <Input
                    id="priority"
                    type="number"
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                    placeholder="0"
                  />
                </div>

                <div>
                  <Label htmlFor="minQty">Min Quantity</Label>
                  <Input
                    id="minQty"
                    type="number"
                    min="0"
                    value={minQuantity}
                    onChange={(e) => setMinQuantity(e.target.value)}
                  />
                </div>

                <div>
                  <Label htmlFor="maxQty">Max Quantity</Label>
                  <Input
                    id="maxQty"
                    type="number"
                    min="0"
                    value={maxQuantity}
                    onChange={(e) => setMaxQuantity(e.target.value)}
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Switch
                    id="ruleActive"
                    checked={isActive}
                    onCheckedChange={setIsActive}
                  />
                  <Label htmlFor="ruleActive">Active</Label>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createRule.isPending}>
                  {createRule.isPending ? 'Creating...' : 'Add Rule'}
                </Button>
              </div>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
